// Teams - Groupes privés type Workplace by Meta
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Link as LinkIcon, Copy, Check, X, Send, Image,
  MoreHorizontal, Heart, MessageCircle, Trash2, UserPlus, Settings,
  ChevronRight, Clock, Crown, LogOut, CheckSquare, Circle, CheckCircle2,
  Calendar, AlertCircle, User, Shield, ArrowLeft, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { API_URL } from '../../config/api';

// Couleurs prédéfinies pour les teams
const CIRCLE_COLORS = [
  { id: 'orange', color: '#E8650A', label: 'Orange' },
  { id: 'blue', color: '#3B82F6', label: 'Bleu' },
  { id: 'green', color: '#22C55E', label: 'Vert' },
  { id: 'purple', color: '#8B5CF6', label: 'Violet' },
  { id: 'pink', color: '#EC4899', label: 'Rose' },
  { id: 'teal', color: '#14B8A6', label: 'Turquoise' },
];

// Icônes prédéfinies
const CIRCLE_ICONS = [
  { id: 'users', icon: Users, label: 'Groupe' },
  { id: 'heart', icon: Heart, label: 'Famille' },
  { id: 'crown', icon: Crown, label: 'VIP' },
];

// Statuts des tâches
const TASK_STATUSES = {
  todo: { label: 'À faire', color: 'bg-gray-100 text-gray-700', icon: Circle },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
  done: { label: 'Terminé', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

export default function CerclesPage() {
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [isPostingLoading, setIsPostingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('feed'); // 'feed', 'tasks', or 'events'
  const [members, setMembers] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);

  const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');

  // Charger les cercles
  useEffect(() => {
    loadCircles();
  }, []);

  // Charger le feed, les tâches et les événements quand un cercle est sélectionné
  useEffect(() => {
    if (selectedCircle) {
      loadCirclePosts(selectedCircle.id);
      loadCircleTasks(selectedCircle.id);
      loadCircleMembers(selectedCircle.id);
      loadCircleEvents(selectedCircle.id);
    }
  }, [selectedCircle]);

  const loadCircles = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCircles(data);
        if (data.length > 0 && !selectedCircle) {
          setSelectedCircle(data[0]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement cercles:', error);
    }
    setIsLoading(false);
  };

  const loadCircleMembers = async (circleId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${circleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Erreur chargement membres:', error);
    }
  };

  const loadCircleTasks = async (circleId) => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/ecosystem/circles/${circleId}/tasks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/ecosystem/circles/${circleId}/tasks/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData);
      }
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setTaskStats(statsData);
      }
    } catch (error) {
      console.error('Erreur chargement tâches:', error);
    }
  };

  const loadCircleEvents = async (circleId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${circleId}/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    }
  };

  const loadCirclePosts = async (circleId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${circleId}/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Erreur chargement posts:', error);
    }
  };

  const handleCreateCircle = async (name, description, color, icon) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, description, color, icon })
      });
      
      if (res.ok) {
        const newCircle = await res.json();
        setCircles([newCircle, ...circles]);
        setSelectedCircle(newCircle);
        setShowCreateModal(false);
        toast.success(`Cercle "${name}" créé !`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDeleteCircle = async (circleId) => {
    if (!confirm('Supprimer ce cercle et tout son contenu ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${circleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setCircles(circles.filter(c => c.id !== circleId));
        if (selectedCircle?.id === circleId) {
          setSelectedCircle(circles.find(c => c.id !== circleId) || null);
        }
        toast.success('Team supprimée');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || !selectedCircle) return;
    
    setIsPostingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${selectedCircle.id}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newPostContent,
          post_type: 'text'
        })
      });
      
      if (res.ok) {
        const newPost = await res.json();
        setPosts([newPost, ...posts]);
        setNewPostContent('');
        toast.success('Publié !');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
    setIsPostingLoading(false);
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const { liked } = await res.json();
        setPosts(posts.map(p => {
          if (p.id === postId) {
            return {
              ...p,
              user_liked: liked,
              likes_count: liked ? p.likes_count + 1 : p.likes_count - 1
            };
          }
          return p;
        }));
      }
    } catch (error) {
      console.error('Erreur like:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setPosts(posts.filter(p => p.id !== postId));
        toast.success('Post supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyInviteLink = () => {
    if (!selectedCircle) return;
    const link = `${window.location.origin}/rejoindre/${selectedCircle.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };

  // ===== TASK HANDLERS =====
  const handleCreateTask = async (taskData) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${selectedCircle.id}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(taskData)
      });
      
      if (res.ok) {
        const newTask = await res.json();
        setTasks([newTask, ...tasks]);
        setShowTaskModal(false);
        toast.success('Tâche créée !');
        loadCircleTasks(selectedCircle.id); // Refresh stats
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/tasks/${taskId}?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const updatedTask = await res.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        loadCircleTasks(selectedCircle.id); // Refresh stats
        toast.success('Statut mis à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Supprimer cette tâche ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTasks(tasks.filter(t => t.id !== taskId));
        loadCircleTasks(selectedCircle.id); // Refresh stats
        toast.success('Tâche supprimée');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ===== EVENT HANDLERS =====
  const handleCreateEvent = async (eventData) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${selectedCircle.id}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });
      
      if (res.ok) {
        const newEvent = await res.json();
        setEvents([...events, newEvent].sort((a, b) => new Date(a.start_date) - new Date(b.start_date)));
        setShowEventModal(false);
        toast.success('Événement créé !');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleToggleAttendance = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/events/${eventId}/attend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const { attending, attendees_count } = await res.json();
        setEvents(events.map(e => e.id === eventId ? { ...e, user_attending: attending, attendees_count } : e));
        toast.success(attending ? 'Tu participes !' : 'Participation annulée');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Supprimer cet événement ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setEvents(events.filter(e => e.id !== eventId));
        toast.success('Événement supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Empty state
  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-5">
          <Users size={40} className="text-orange-500" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-6">Mes Groupes Privés</h2>
        
        {/* Arguments clés - Mise en avant premium */}
        <div className="w-full max-w-sm mb-6 space-y-2.5">
          <div className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-4 py-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Check size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">100% Privé & Sécurisé</p>
              <p className="text-xs text-gray-500">Tes conversations restent entre vous</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-4 py-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <User size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">1 Personne = 1 Compte</p>
              <p className="text-xs text-gray-500">Zéro faux profil, que des vrais</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-xl px-4 py-3">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">Zéro Donnée Sauvegardée</p>
              <p className="text-xs text-gray-500">On ne revend rien, jamais</p>
            </div>
          </div>
        </div>
        
        <Button className="bg-[#E8650A] hover:bg-[#d45a09] w-full max-w-sm h-12 text-base font-semibold">
          Connecte-toi pour commencer
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)] bg-gray-50">
      {/* Sidebar - Liste des cercles (collapsible sur mobile) */}
      <div className={`${selectedCircle && 'hidden lg:flex'} lg:w-80 w-full bg-white border-r border-gray-200 flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gray-900">Mes Groupes</h2>
            <Button 
              size="sm" 
              className="bg-[#E8650A] hover:bg-[#d45a09] active:scale-95 transition-transform touch-manipulation"
              onClick={() => setShowCreateModal(true)}
              style={{ minHeight: '40px' }}
            >
              <Plus size={18} className="mr-1" />
              Créer
            </Button>
          </div>
          <p style={{ fontSize: '12px', color: '#888888', fontFamily: "'DM Sans', sans-serif", lineHeight: '1.4' }}>
            100% privé & sécurisé · 1 personne = 1 compte · Zéro faux profil
          </p>
        </div>

        {/* Liste des cercles */}
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {circles.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={24} className="text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm mb-4">Aucune team pour le moment</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowCreateModal(true)}
                className="touch-manipulation"
              >
                Créer ma première team
              </Button>
            </div>
          ) : (
            <div className="p-2 space-y-1.5">
              {circles.map(circle => (
                <button
                  key={circle.id}
                  onClick={() => setSelectedCircle(circle)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl transition-all active:scale-[0.98] touch-manipulation ${
                    selectedCircle?.id === circle.id 
                      ? 'bg-orange-50 border border-orange-200' 
                      : 'hover:bg-gray-50 active:bg-gray-100'
                  }`}
                  style={{ minHeight: '64px' }}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                    style={{ backgroundColor: circle.color || '#E8650A' }}
                  >
                    {circle.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{circle.name}</p>
                    <p className="text-sm text-gray-500">{circle.member_count} membre{circle.member_count > 1 ? 's' : ''}</p>
                  </div>
                  {circle.unread_count > 0 && (
                    <span className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                      {circle.unread_count > 9 ? '9+' : circle.unread_count}
                    </span>
                  )}
                  <ChevronRight size={20} className="text-gray-400 flex-shrink-0 lg:hidden" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main - Feed du cercle sélectionné */}
      <div className={`${!selectedCircle && 'hidden lg:flex'} flex-1 flex flex-col`}>
        {selectedCircle ? (
          <>
            {/* Header du cercle - avec bouton retour sur mobile */}
            <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  {/* Bouton retour mobile */}
                  <button 
                    onClick={() => setSelectedCircle(null)}
                    className="lg:hidden p-2 -ml-1 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                    style={{ minWidth: '40px', minHeight: '40px' }}
                  >
                    <ChevronRight size={22} className="text-gray-600 rotate-180" />
                  </button>
                  <div 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0"
                    style={{ backgroundColor: selectedCircle.color || '#E8650A' }}
                  >
                    {selectedCircle.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{selectedCircle.name}</h1>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {selectedCircle.member_count} membre{selectedCircle.member_count > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowInviteModal(true)}
                    className="touch-manipulation hidden sm:flex"
                  >
                    <UserPlus size={16} className="mr-1" />
                    Inviter
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowInviteModal(true)}
                    className="touch-manipulation sm:hidden w-10 h-10"
                  >
                    <UserPlus size={18} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowMembersModal(true)}
                    className="touch-manipulation hidden sm:flex"
                  >
                    <Users size={16} className="mr-1" />
                    Membres
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowMembersModal(true)}
                    className="touch-manipulation sm:hidden w-10 h-10"
                  >
                    <Users size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 touch-manipulation w-10 h-10"
                    onClick={() => handleDeleteCircle(selectedCircle.id)}
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </div>
              
              {/* Onglets Feed / Tâches / Événements - scrollable sur mobile */}
              <div className="flex gap-0.5 sm:gap-1 mt-3 sm:mt-4 border-b border-gray-100 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <button
                  onClick={() => setActiveTab('feed')}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap touch-manipulation ${
                    activeTab === 'feed' 
                      ? 'border-[#E8650A] text-[#E8650A]' 
                      : 'border-transparent text-gray-500 active:text-gray-700'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  <MessageCircle size={18} />
                  <span>Feed</span>
                </button>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap touch-manipulation ${
                    activeTab === 'tasks' 
                      ? 'border-[#E8650A] text-[#E8650A]' 
                      : 'border-transparent text-gray-500 active:text-gray-700'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  <CheckSquare size={18} />
                  <span>Tâches</span>
                  {taskStats && taskStats.my_pending_tasks > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                      {taskStats.my_pending_tasks}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('events')}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap touch-manipulation ${
                    activeTab === 'events' 
                      ? 'border-[#E8650A] text-[#E8650A]' 
                      : 'border-transparent text-gray-500 active:text-gray-700'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  <Calendar size={18} />
                  Événements
                  {events.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full">
                      {events.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Contenu selon l'onglet actif */}
            {activeTab === 'feed' ? (
              <>
                {/* Zone de publication */}
                <div className="bg-white border-b border-gray-200 p-4">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <Users size={18} className="text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                        placeholder="Partage quelque chose avec la team..."
                        className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        rows={2}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-2">
                          <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                            <Image size={20} />
                          </button>
                        </div>
                        <Button 
                          className="bg-[#E8650A] hover:bg-[#d45a09]"
                          disabled={!newPostContent.trim() || isPostingLoading}
                          onClick={handleCreatePost}
                        >
                          {isPostingLoading ? 'Publication...' : 'Publier'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feed */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle size={24} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500">Aucune publication pour le moment</p>
                  <p className="text-sm text-gray-400">Sois le premier à partager quelque chose !</p>
                </div>
              ) : (
                posts.map(post => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border border-gray-200 p-4"
                  >
                    <div className="flex items-start gap-3">
                      {post.author.avatar ? (
                        <img 
                          src={post.author.avatar} 
                          alt={post.author.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="font-bold text-gray-600">
                            {post.author.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-gray-900">{post.author.name}</span>
                            <span className="text-gray-500 text-sm ml-2">
                              {new Date(post.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <button 
                            onClick={() => handleDeletePost(post.id)}
                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="text-gray-800 mt-2 whitespace-pre-wrap">{post.content}</p>
                        
                        {/* Media */}
                        {post.media && post.media.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {post.media.map((url, idx) => (
                              <img 
                                key={idx}
                                src={url}
                                alt=""
                                className="rounded-lg object-cover w-full h-48"
                              />
                            ))}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                          <button 
                            onClick={() => handleLikePost(post.id)}
                            className={`flex items-center gap-1 text-sm ${
                              post.user_liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                            }`}
                          >
                            <Heart size={18} className={post.user_liked ? 'fill-current' : ''} />
                            <span>{post.likes_count}</span>
                          </button>
                          <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-500">
                            <MessageCircle size={18} />
                            <span>{post.comments_count}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
              </>
            ) : activeTab === 'tasks' ? (
              /* ===== ONGLET TÂCHES ===== */
              <>
                {/* Header Tâches avec stats et bouton créer */}
                <div className="bg-white border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      {taskStats && (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                            <span className="text-gray-600">{taskStats.todo} à faire</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <span className="text-gray-600">{taskStats.in_progress} en cours</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span className="text-gray-600">{taskStats.done} terminées</span>
                          </div>
                        </>
                      )}
                    </div>
                    <Button 
                      className="bg-[#E8650A] hover:bg-[#d45a09]"
                      onClick={() => setShowTaskModal(true)}
                    >
                      <Plus size={16} className="mr-1" />
                      Nouvelle tâche
                    </Button>
                  </div>
                </div>

                {/* Liste des tâches */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {tasks.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckSquare size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-500 mb-2">Aucune tâche pour le moment</p>
                      <p className="text-gray-400 text-sm">Crée une tâche et assigne-la à un membre de la team</p>
                    </div>
                  ) : (
                    tasks.map(task => {
                      const statusInfo = TASK_STATUSES[task.status] || TASK_STATUSES.todo;
                      const StatusIcon = statusInfo.icon;
                      
                      return (
                        <motion.div
                          key={task.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start gap-3">
                            {/* Status toggle */}
                            <button
                              onClick={() => {
                                const nextStatus = task.status === 'todo' ? 'in_progress' : task.status === 'in_progress' ? 'done' : 'todo';
                                handleUpdateTaskStatus(task.id, nextStatus);
                              }}
                              className="mt-0.5"
                            >
                              <StatusIcon 
                                size={22} 
                                className={`${task.status === 'done' ? 'text-green-500' : task.status === 'in_progress' ? 'text-blue-500' : 'text-gray-300'} hover:scale-110 transition-transform`}
                              />
                            </button>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className={`font-medium ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  {task.title}
                                </h3>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                              
                              {task.description && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                              )}
                              
                              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                                {/* Assigné */}
                                <div className="flex items-center gap-1">
                                  {task.assignee ? (
                                    <>
                                      {task.assignee.avatar ? (
                                        <img src={task.assignee.avatar} alt="" className="w-5 h-5 rounded-full" />
                                      ) : (
                                        <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                                          <User size={12} className="text-gray-500" />
                                        </div>
                                      )}
                                      <span>{task.assignee.name}</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Non assigné</span>
                                  )}
                                </div>
                                
                                {/* Date limite */}
                                {task.due_date && (
                                  <div className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    <span>{new Date(task.due_date).toLocaleDateString('fr-FR')}</span>
                                  </div>
                                )}
                                
                                {/* Créateur */}
                                <div className="flex items-center gap-1 ml-auto">
                                  <span className="text-gray-400">par</span>
                                  <span>{task.creator?.name}</span>
                                </div>
                                
                                {/* Supprimer */}
                                <button
                                  onClick={() => handleDeleteTask(task.id)}
                                  className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </>
            ) : activeTab === 'events' ? (
              /* ===== ONGLET ÉVÉNEMENTS ===== */
              <>
                {/* Header Événements */}
                <div className="bg-white border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      {events.length} événement{events.length > 1 ? 's' : ''} à venir
                    </p>
                    <Button 
                      className="bg-[#E8650A] hover:bg-[#d45a09]"
                      onClick={() => setShowEventModal(true)}
                    >
                      <Plus size={16} className="mr-1" />
                      Nouvel événement
                    </Button>
                  </div>
                </div>

                {/* Liste des événements */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {events.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Calendar size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-500 mb-2">Aucun événement prévu</p>
                      <p className="text-gray-400 text-sm">Crée un événement pour le partager avec la team</p>
                    </div>
                  ) : (
                    events.map(event => {
                      const eventDate = new Date(event.start_date);
                      const isUpcoming = eventDate > new Date();
                      const userAttending = event.attendees?.includes(localStorage.getItem('buyer_user') ? JSON.parse(localStorage.getItem('buyer_user')).id : '');
                      
                      return (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex gap-4">
                            {/* Date box */}
                            <div className="flex-shrink-0 w-14 h-14 bg-orange-50 rounded-xl flex flex-col items-center justify-center">
                              <span className="text-xs font-medium text-orange-600 uppercase">
                                {eventDate.toLocaleDateString('fr-FR', { month: 'short' })}
                              </span>
                              <span className="text-xl font-bold text-orange-700">
                                {eventDate.getDate()}
                              </span>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h3 className="font-medium text-gray-900">{event.title}</h3>
                                  <p className="text-sm text-gray-500 mt-0.5">
                                    {event.is_all_day ? 'Toute la journée' : eventDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    {event.location && ` • ${event.location}`}
                                  </p>
                                </div>
                                {isUpcoming && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                    À venir
                                  </span>
                                )}
                              </div>
                              
                              {event.description && (
                                <p className="text-sm text-gray-500 mt-2 line-clamp-2">{event.description}</p>
                              )}
                              
                              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Users size={12} />
                                  <span>{event.attendees?.length || 0} participant{(event.attendees?.length || 0) > 1 ? 's' : ''}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">par</span>
                                  <span>{event.creator?.name}</span>
                                </div>
                                
                                <div className="ml-auto flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleAttendance(event.id)}
                                    className={userAttending ? 'text-green-600' : 'text-gray-500'}
                                  >
                                    <Check size={14} className="mr-1" />
                                    {userAttending ? 'Je participe' : 'Participer'}
                                  </Button>
                                  <button
                                    onClick={() => handleDeleteEvent(event.id)}
                                    className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Users size={32} className="text-gray-400" />
            </div>
            <p className="text-gray-500">Sélectionne une team ou crées-en une nouvelle</p>
          </div>
        )}
      </div>

      {/* Modal - Créer un cercle */}
      <CreateCircleModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateCircle}
      />

      {/* Modal - Inviter */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        circle={selectedCircle}
        onCopyLink={copyInviteLink}
      />

      {/* Modal - Membres */}
      <MembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        circle={selectedCircle}
        token={token}
      />

      {/* Modal - Créer une tâche */}
      <CreateTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onCreate={handleCreateTask}
        members={members}
      />

      {/* Modal - Créer un événement */}
      <CreateEventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onCreate={handleCreateEvent}
        members={members}
      />
    </div>
  );
}

// Modal de création de cercle
function CreateCircleModal({ isOpen, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#E8650A');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Détection du clavier
  useEffect(() => {
    if (!isOpen || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setIsLoading(true);
    await onCreate(name, description, color, 'users');
    setIsLoading(false);
    setName('');
    setDescription('');
  };

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[15px] font-bold text-gray-900">Créer une team</h1>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          <form id="create-circle-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                Nom de la team *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Famille, Équipe projet, Copains rugby..."
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                Description (optionnel)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="À quoi sert cette team ?"
                className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                Couleur
              </label>
              <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                {CIRCLE_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setColor(c.color)}
                    className={`flex-shrink-0 w-10 h-10 rounded-full transition-transform ${
                      color === c.color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: c.color }}
                  />
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
          >
            <Sparkles size={16} />
            Créer avec Abracada
          </button>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              form="create-circle-form"
              disabled={isLoading || !name.trim()}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
            >
              {isLoading ? 'Création...' : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Créer une team</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la team *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Famille, Équipe projet, Copains rugby..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="À quoi sert cette team ?"
              className="w-full p-3 border border-gray-200 rounded-xl resize-none"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Couleur
            </label>
            <div className="flex gap-2">
              {CIRCLE_COLORS.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColor(c.color)}
                  className={`w-10 h-10 rounded-full transition-transform ${
                    color === c.color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: c.color }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-[#E8650A] hover:bg-[#d45a09]"
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? 'Création...' : 'Créer la team'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Modal d'invitation
function InviteModal({ isOpen, onClose, circle, onCopyLink }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen || !circle) return null;

  const inviteLink = `${window.location.origin}/rejoindre/${circle.invite_code}`;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Inviter des personnes</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="text-center mb-6">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4"
            style={{ backgroundColor: circle.color || '#E8650A' }}
          >
            {circle.name.charAt(0).toUpperCase()}
          </div>
          <h3 className="font-semibold text-gray-900">{circle.name}</h3>
          <p className="text-sm text-gray-500">Partage ce lien pour inviter des membres</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-gray-500 mb-2">Lien d'invitation</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm text-gray-700 truncate">{inviteLink}</code>
            <button
              onClick={handleCopy}
              className={`p-2 rounded-lg transition-colors ${
                copied ? 'bg-green-100 text-green-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
              }`}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center">
          Les personnes qui cliquent sur ce lien pourront rejoindre la team après connexion.
        </p>
      </motion.div>
    </div>
  );
}

// Modal des membres
function MembersModal({ isOpen, onClose, circle, token }) {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && circle) {
      loadMembers();
    }
  }, [isOpen, circle]);

  const loadMembers = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/${circle.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Erreur chargement membres:', error);
    }
    setIsLoading(false);
  };

  if (!isOpen || !circle) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Membres ({members.length})</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            members.map(member => (
              <div key={member.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl">
                {member.avatar ? (
                  <img 
                    src={member.avatar} 
                    alt={member.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="font-bold text-gray-600">
                      {member.name?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{member.name}</p>
                  <p className="text-xs text-gray-500">
                    {member.role === 'admin' ? 'Admin' : 'Membre'}
                    {member.joined_at && ` · Depuis ${new Date(member.joined_at).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>
                {member.role === 'admin' && (
                  <Crown size={16} className="text-orange-500" />
                )}
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}


// Modal de création de tâche
function CreateTaskModal({ isOpen, onClose, onCreate, members }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    setIsLoading(true);
    await onCreate({
      title: title.trim(),
      description: description.trim(),
      assigned_to: assignedTo || null,
      due_date: dueDate || null
    });
    setIsLoading(false);
    setTitle('');
    setDescription('');
    setAssignedTo('');
    setDueDate('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Nouvelle tâche</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre de la tâche *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Acheter le cadeau, Réserver le resto..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détails supplémentaires..."
              className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigner à
            </label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">Non assigné</option>
              {members.map(member => (
                <option key={member.user_id} value={member.user_id}>
                  {member.display_name || member.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date limite (optionnel)
            </label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#E8650A] hover:bg-[#d45a09]"
              disabled={!title.trim() || isLoading}
            >
              {isLoading ? 'Création...' : 'Créer la tâche'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


// Modal de création d'événement
function CreateEventModal({ isOpen, onClose, onCreate, members = [] }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [location, setLocation] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Réinitialiser les membres sélectionnés quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      // Par défaut, tous les membres sont sélectionnés
      setSelectedMembers(members.map(m => m.user_id));
    }
  }, [isOpen, members]);

  const toggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => setSelectedMembers(members.map(m => m.user_id));
  const selectNone = () => setSelectedMembers([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !startDate || selectedMembers.length === 0) return;
    
    setIsLoading(true);
    
    // Construct ISO date string
    let isoDate = startDate;
    if (!isAllDay && startTime) {
      isoDate = `${startDate}T${startTime}:00`;
    } else {
      isoDate = `${startDate}T00:00:00`;
    }
    
    await onCreate({
      title: title.trim(),
      description: description.trim() || null,
      start_date: isoDate,
      location: location.trim() || null,
      is_all_day: isAllDay,
      invited_members: selectedMembers
    });
    
    setIsLoading(false);
    setTitle('');
    setDescription('');
    setStartDate('');
    setStartTime('');
    setLocation('');
    setIsAllDay(false);
    setSelectedMembers([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Nouvel événement</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Anniversaire Tatie, Dîner en famille..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détails de l'événement..."
              className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            {!isAllDay && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure
                </label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isAllDay"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
              className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
            />
            <label htmlFor="isAllDay" className="text-sm text-gray-700">
              Toute la journée
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu (optionnel)
            </label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ex: Chez Tatie, Restaurant Le Petit..."
            />
          </div>

          {/* Sélection des participants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Qui inviter ? *
              </label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAll} className="text-orange-600 hover:underline">
                  Tous
                </button>
                <span className="text-gray-300">|</span>
                <button type="button" onClick={selectNone} className="text-gray-500 hover:underline">
                  Aucun
                </button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
              {members.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-2">Aucun membre dans cette team</p>
              ) : (
                members.map(member => (
                  <label 
                    key={member.user_id}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member.user_id)}
                      onChange={() => toggleMember(member.user_id)}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                    {member.profile_photo ? (
                      <img src={member.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User size={14} className="text-gray-500" />
                      </div>
                    )}
                    <span className="text-sm text-gray-700">{member.display_name || member.email}</span>
                    {member.role === 'admin' && (
                      <span className="ml-auto text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Admin</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedMembers.length} membre{selectedMembers.length > 1 ? 's' : ''} sélectionné{selectedMembers.length > 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#E8650A] hover:bg-[#d45a09]"
              disabled={!title.trim() || !startDate || selectedMembers.length === 0 || isLoading}
            >
              {isLoading ? 'Création...' : 'Créer l\'événement'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
